import type { FolderTreeNode } from "../types/project-storage.types";

export function sortBySortOrder<T extends { sortOrder: number }>(
  items: T[]
): T[] {
  return [...items].sort((a, b) => a.sortOrder - b.sortOrder);
}

export function findFolderInTree(
  tree: FolderTreeNode[],
  folderId: string
): FolderTreeNode | null {
  for (const node of tree) {
    if (node.id === folderId) return node;
    const found = findFolderInTree(node.children, folderId);
    if (found) return found;
  }
  return null;
}

export function collectAllFolderIds(tree: FolderTreeNode[]): string[] {
  const ids: string[] = [];

  function walk(nodes: FolderTreeNode[]) {
    for (const node of nodes) {
      ids.push(node.id);
      walk(node.children);
    }
  }

  walk(tree);
  return ids;
}

export function getParentFolderId(
  tree: FolderTreeNode[],
  folderId: string
): string | null {
  const ancestors = collectAncestorIds(tree, folderId);
  return ancestors.length > 0 ? ancestors[ancestors.length - 1] : null;
}

export function collectAncestorIds(
  tree: FolderTreeNode[],
  folderId: string
): string[] {
  function walk(
    nodes: FolderTreeNode[],
    trail: string[]
  ): string[] | null {
    for (const node of nodes) {
      const nextTrail = [...trail, node.id];
      if (node.id === folderId) return trail;
      const found = walk(node.children, nextTrail);
      if (found) return found;
    }
    return null;
  }

  return walk(tree, []) ?? [];
}

export function findFolderIdByPathNames(
  tree: FolderTreeNode[],
  pathNames: string[]
): string | null {
  let currentLevel = tree;
  let matchedNode: FolderTreeNode | null = null;

  for (const pathName of pathNames) {
    matchedNode =
      currentLevel.find((node) => node.name === pathName) ?? null;
    if (!matchedNode) return null;
    currentLevel = matchedNode.children;
  }

  return matchedNode?.id ?? null;
}

export function resolveFolderDisplayName(
  tree: FolderTreeNode[],
  folderName: string
): string {
  function walk(nodes: FolderTreeNode[]): string | null {
    for (const node of nodes) {
      if (node.name === folderName) return node.displayName;
      const found = walk(node.children);
      if (found) return found;
    }
    return null;
  }

  return walk(tree) ?? folderName;
}

export function buildBreadcrumbSegments(
  tree: FolderTreeNode[],
  folder: {
    id: string;
    displayName: string;
    pathNames: string[];
  }
): Array<{ id: string; label: string }> {
  const segments = folder.pathNames.map((pathName, index) => ({
    id:
      findFolderIdByPathNames(tree, folder.pathNames.slice(0, index + 1)) ??
      folder.id,
    label: resolveFolderDisplayName(tree, pathName),
  }));

  const lastSegment = segments[segments.length - 1];
  if (!lastSegment || lastSegment.id !== folder.id) {
    segments.push({ id: folder.id, label: folder.displayName });
  }

  return segments;
}
